/* eslint-disable */
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { TeamLineupsContext } from 'widgets/mc-team-lineups/js/components/MCTeamLineups';
import { TAPlayerList } from './TAPlayerList';

/**
 * Renders the ins/outs data for team announcements.
 *
 * @param {object} props - The props.
 * @param {Array} [props.playersInHome] - Players in for home team.
 * @param {Array} [props.playersOutHome] - Players out for home team.
 * @param {Array} [props.playersInAway] - Players in for away team.
 * @param {Array} [props.playersOutAway] - Players out for away team.
 * @param {string} props.teamDisplay - Which team to display ('both', 'home', or 'away').
 * @param {boolean} [props.isAFLW] - Check if competition is AFLW.
 * @param {string|number} [props.homeTeamId] - Home team ID.
 * @param {string|number} [props.awayTeamId] - Away team ID.
 * @returns {React.JSX.Element} The rendered component.
 */
export const TeamAnnouncementsFieldInsOuts = ({
    playersInHome,
    playersOutHome,
    playersInAway,
    playersOutAway,
    teamDisplay,
    isAFLW,
    homeTeamId,
    awayTeamId
}) => {
    const teamLineupsContext = useContext(TeamLineupsContext);
    const isSingle = teamDisplay === 'home' || teamDisplay === 'away';
    const isHome = teamDisplay === 'home';

    const inArray = isHome ? playersInHome : playersInAway;
    const outArray = isHome ? playersOutHome : playersOutAway;
    const clubId = isHome ? homeTeamId : awayTeamId;

    const renderSection = (
        typeLabel,
        singleInArray,
        homePlayers,
        awayPlayers,
        prefix
    ) => (
        <div className="team-announcements-field__footer-section">
            {isSingle && (
                <>
                    <span className="team-announcements-field__footer-section-label team-announcements-field__footer-section-label--in-out">
                        <span
                            className={`team-announcements-field__ins-outs-tag team-announcements-field__ins-outs-tag--${typeLabel} content-tag`}
                        >
                            {PULSE.I18N.lookup(
                                `label.teamlineups.player${
                                    typeLabel === 'in' ? 'In' : 'Out'
                                }`
                            )}
                        </span>
                    </span>
                    <span className="team-announcements-field__footer-section-values">
                        <TAPlayerList
                            players={singleInArray}
                            prefix={`Single${prefix}`}
                            isAFLW={isAFLW}
                            clubId={clubId}
                            context={teamLineupsContext}
                        />
                    </span>
                </>
            )}

            {teamDisplay === 'both' && (
                <>
                    <span className="team-announcements-field__footer-section-values team-announcements-field__footer-section-values--home">
                        <TAPlayerList
                            players={homePlayers}
                            prefix={`Home${prefix}`}
                            isAFLW={isAFLW}
                            clubId={homeTeamId}
                            context={teamLineupsContext}
                        />
                    </span>

                    <span className="team-announcements-field__footer-section-label team-announcements-field__footer-section-label--in-out">
                        <span
                            className={`team-announcements-field__ins-outs-tag team-announcements-field__ins-outs-tag--${typeLabel} content-tag`}
                        >
                            {PULSE.I18N.lookup(
                                `label.teamlineups.player${
                                    typeLabel === 'in' ? 'In' : 'Out'
                                }`
                            )}
                        </span>
                    </span>

                    <span className="team-announcements-field__footer-section-values team-announcements-field__footer-section-values--away">
                        <TAPlayerList
                            players={awayPlayers}
                            prefix={`Away${prefix}`}
                            isAFLW={isAFLW}
                            clubId={awayTeamId}
                            context={teamLineupsContext}
                        />
                    </span>
                </>
            )}
        </div>
    );

    return (
        <div className="team-announcements-field__footer-section-wrapper team-announcements-field__footer-section-wrapper--in-out">
            {renderSection('in', inArray, playersInHome, playersInAway, 'In')}
            {renderSection(
                'out',
                outArray,
                playersOutHome,
                playersOutAway,
                'Out'
            )}
        </div>
    );
};

TeamAnnouncementsFieldInsOuts.propTypes = {
    playersInHome: PropTypes.array,
    playersOutHome: PropTypes.array,
    playersInAway: PropTypes.array,
    playersOutAway: PropTypes.array,
    teamDisplay: PropTypes.oneOf(['both', 'home', 'away']),
    isAFLW: PropTypes.bool,
    homeTeamId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    awayTeamId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};
